import { observer } from "mobx-react-lite";
import { useContext } from "react";

import { CLAIM_FORM_ID } from "@modules/acc/screens/claim/components/ClaimForm.types.ts";
import { ClaimContext } from "@modules/acc/screens/claim/context/ClaimContext.ts";
import { AccidentAndEmploymentDetailsDraftValidator } from "@modules/acc/screens/claim/validators/AccidentAndEmploymentDetailsDraftValidator.tsx";
import { PatientDeclarationDraftValidator } from "@modules/acc/screens/claim/validators/PatientDeclarationDraftValidator.tsx";
import { PersonalDetailsDraftValidator } from "@modules/acc/screens/claim/validators/PersonalDetailsDraftValidator.tsx";
import { ClaimFormValues } from "@shared-types/acc/claim-form-values.type.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { usePersistedHelper } from "@ui-components/hooks/usePersistedHelper.ts";

import { ClaimFormProps } from "../types/claim.types.ts";
import { InjuryDiagnosisAndAssistanceDraftValidator } from "../validators/InjuryDiagnosisAndAssistanceDraftValidator.tsx";
import { ProviderDeclarationDraftValidator } from "../validators/ProviderDeclarationDraftValidator.tsx";
import { formStyles } from "./ClaimForm.styles.ts";
import { ClaimFormInner } from "./ClaimFormInner.tsx";

const personalDetailsDraftValidator = new PersonalDetailsDraftValidator();
const patientDeclarationDraftValidator = new PatientDeclarationDraftValidator();

const injuryDiagnosisDraftValidator =
  new InjuryDiagnosisAndAssistanceDraftValidator();

const accidentAndEmploymentDetailsDraftValidator =
  new AccidentAndEmploymentDetailsDraftValidator();

const ClaimFormBase: React.FC<ClaimFormProps> = observer(
  ({ header, initialValues, onSubmitSucceeded, children, ...rest }) => {
    const {
      handleSubmit,
      handleSubmitSucceeded,
      getInitialFormValues,
      provider
    } = useContext(ClaimContext);

    // this form's initial values keep changing when the parent component rerenders
    //  so instead we need to persist them so that they do not change
    const _initialValues = usePersistedHelper(
      () => initialValues || getInitialFormValues()
    );

    return (
      <SubmissionForm<ClaimFormValues>
        {...rest}
        formName="claim-form"
        subscription={{}}
        onSubmitSucceeded={(values, form) => {
          handleSubmitSucceeded(values);
          onSubmitSucceeded && onSubmitSucceeded(values, form);
        }}
        onSubmit={handleSubmit}
        styles={formStyles}
        hideButtons
        initialValues={_initialValues}
        validate={values => {
          const providerDeclarationDraftValidator =
            new ProviderDeclarationDraftValidator(provider?.contractTypes);

          return {
            ...patientDeclarationDraftValidator.validate(values),
            ...personalDetailsDraftValidator.validate(values),
            ...injuryDiagnosisDraftValidator.validate(values),
            ...accidentAndEmploymentDetailsDraftValidator.validate(values),
            ...providerDeclarationDraftValidator.validate(values)
          };
        }}
        formId={CLAIM_FORM_ID}
      >
        {() => <ClaimFormInner header={header}>{children}</ClaimFormInner>}
      </SubmissionForm>
    );
  }
);

const ClaimForm = withFetch(
  x => [
    x.acc.ref.accidentLocations.load(),
    x.acc.ref.accidentScenes.load(),
    x.acc.ref.occupations.load(),
    x.acc.ref.sports.load()
  ],
  ClaimFormBase
);

// ⚠ It should be exported as default since it is used for React.lazy
// eslint-disable-next-line import/no-default-export
export default ClaimForm;
